import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { EntrepreneurCurrentVenture } from '@interfaces';
import { FuncService } from '../../../../../../services';
import { PersonFinderWidget } from '../../../../search';


@Component({
	selector: 'add-entrepreneur-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		PersonFinderWidget,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './add-entrepreneur.dialog.html'
})

export class AddEntrepreneurDialog {

	public dialogRef = inject(MatDialogRef<AddEntrepreneurDialog>);
	public dialogContext: DialogContext<{ accVentureId: number, ventureName: string }> = inject(MAT_DIALOG_DATA);
	private func = inject(FuncService);

	public selectedPersonId: number | undefined = undefined;
	public assignedVentures = signal<EntrepreneurCurrentVenture[] | undefined>(undefined);

	public actions = computed<DialogAction[]>(() => {
		const actions: DialogAction[] = [];

		const assignedVentures = this.assignedVentures();

		// Can add only if all assigned ventures are Dropped Out or Never Started or None
		const canAddEntrepreneur = assignedVentures && (assignedVentures.length === 0 || assignedVentures.every(v => v.assignedVentureStatus === 'Dropped Out' || v.assignedVentureStatus === 'Never Started'));

		if (!assignedVentures) {
			actions.push({
				id: 'check',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Check'),
				linkType: 'callback',
				callback: async () => await this.checkEntrepreneurAvailability(),
				willCloseDialog: false,
			});
		} else if (canAddEntrepreneur) {
			actions.push({
				id: 'add',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Add'),
				linkType: 'callback',
				callback: async () => await this.addEntrepreneur(),
				willCloseDialog: true,
			});
		}

		return [
			...actions,
			{
				id: 'close',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Close'),
				linkType: "no-action",
				willCloseDialog: true,
			}
		]
	});

	setSelectedPersonId(personId: number) {
		this.selectedPersonId = personId;
		this.assignedVentures.set(undefined);
	}

	//
	// if assigned to mulitple ventures
	// no assigned ventures - can add
	// assigned to ventures but all are Dropped Out or Never Started - can add
	// assigned to atleast one venture with status Participating or Graduated - cannot add
	//
	async checkEntrepreneurAvailability() {
		if (!this.selectedPersonId) return;
		const data = await this.func.accVenture.isEntrepreneurAvailable({ personId: this.selectedPersonId });
		this.assignedVentures.set(data);
	}

	// to get names and statuses of all assigned ventures
	public assignedVentureInfo = computed(() => {
		const assignedVenture = this.assignedVentures();

		if (!assignedVenture) return undefined;

		return {
			names: assignedVenture.map(v => v.assignedVentureName).join(', '),
			statuses: assignedVenture.map(v => v.assignedVentureStatus).join(', ')
		};
	});

	// to know if assigned to atleast one venture with status Participating or Graduated
	public assignedParticipatingOrGraduatedVenture = computed(() => this.assignedVentures()?.find(v => v.assignedVentureStatus === 'Graduated' || v.assignedVentureStatus === 'Participating'));

	async addEntrepreneur() {
		const data = this.dialogContext.data;
		if (!this.selectedPersonId || !data) return;

		await this.func.accVenture.addEntrepreneur({
			accVentureId: data.accVentureId,
			personId: this.selectedPersonId
		});
	}

}